import React, { useEffect, useMemo, useState } from 'react'
import classNames from 'classnames'
import AppointmentStatusHistory from './AppointmentStatusHistory'
import { useQuery } from '@tanstack/react-query'
import {
    Alert,
    Button,
    Dialog,
    Input,
    Notification,
    toast,
} from 'components/ui'
import {
    APPOINTMENT_OPERATIONAL_STATUSES,
    DEFAULT_APPOINTMENT_PREFERENCES,
    getAppointmentOperationalStatusMeta,
    getStatusBadgeStyle,
} from 'constants/appointmentStatus.constant'
import { apiGetAppointmentPreferences } from 'services/AppointmentPreferencesService'
import { apiUpdateAppointmentStatus } from 'services/ConsultationsService'
import {
    APPOINTMENT_STATUS_NOTE_MAX_LENGTH,
    buildAppointmentStatusPayload,
    getAppointmentStatusHistory,
    getAppointmentStatusInitialValues,
} from './appointmentStatusDialog.utils'

export const appointmentPreferencesQueryKey = ['appointmentPreferences']

const AppointmentStatusDialog = ({ isOpen, appointment, onClose, onSaved }) => {
    const [status, setStatus] = useState('')
    const [note, setNote] = useState('')
    const [error, setError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { data: preferences = DEFAULT_APPOINTMENT_PREFERENCES } = useQuery({
        queryKey: appointmentPreferencesQueryKey,
        queryFn: async () => {
            const response = await apiGetAppointmentPreferences()
            return response.data
        },
        enabled: isOpen,
    })

    const history = useMemo(
        () => getAppointmentStatusHistory(appointment),
        [appointment]
    )
    const initialValues = useMemo(
        () => getAppointmentStatusInitialValues(appointment),
        [appointment]
    )
    const hasChanges =
        status !== initialValues.status ||
        note.trim() !== initialValues.note.trim()

    useEffect(() => {
        if (!isOpen) return

        setStatus(initialValues.status)
        setNote(initialValues.note)
        setError('')
    }, [initialValues, isOpen])

    const handleClose = () => {
        if (!isSubmitting) onClose?.()
    }

    const handleSubmit = async (event) => {
        event.preventDefault()
        if (!status || !appointment?._id || isSubmitting) return

        setError('')
        setIsSubmitting(true)

        try {
            const response = await apiUpdateAppointmentStatus(
                appointment._id,
                buildAppointmentStatusPayload(status, note)
            )

            await onSaved?.(response.data)
            toast.push(
                <Notification
                    title="Statut mis à jour"
                    type="success"
                    duration={2500}
                >
                    Le statut du rendez-vous a été enregistré.
                </Notification>,
                { placement: 'top-center' }
            )
            onClose?.()
        } catch (requestError) {
            setError(
                requestError?.response?.data?.error ||
                    requestError?.response?.data?.message ||
                    "Impossible d'enregistrer le statut. Veuillez réessayer."
            )
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog
            isOpen={Boolean(isOpen)}
            onClose={handleClose}
            onRequestClose={handleClose}
            width={720}
            contentClassName="max-h-[90vh] overflow-y-auto"
        >
            <form onSubmit={handleSubmit}>
                <div className="border-b border-gray-100 pb-4 pr-8 dark:border-gray-600">
                    <h5>Mettre à jour le statut</h5>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-300">
                        Choisissez la prochaine action à effectuer pour ce
                        rendez-vous.
                    </p>
                </div>

                <div
                    className={classNames(
                        'grid gap-6 py-5 md:grid-cols-[minmax(0,1fr)_260px]'
                    )}
                >
                    <div className="min-w-0 space-y-5">
                        <fieldset>
                            <legend className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-100">
                                Nouveau statut
                            </legend>
                            <div className="grid gap-2 sm:grid-cols-2">
                                {APPOINTMENT_OPERATIONAL_STATUSES.map(
                                    (option) => {
                                        const meta =
                                            getAppointmentOperationalStatusMeta(
                                                option.value,
                                                preferences
                                            )
                                        const isSelected =
                                            status === option.value
                                        const descriptionId = `appointment-status-${option.value}-description`

                                        return (
                                            <label
                                                key={option.value}
                                                className={classNames(
                                                    'relative flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-3 transition-colors focus-within:ring-2 focus-within:ring-blue-400',
                                                    isSelected
                                                        ? 'border-transparent'
                                                        : 'border-gray-200 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700'
                                                )}
                                                style={
                                                    isSelected
                                                        ? {
                                                              backgroundColor:
                                                                  getStatusBadgeStyle(
                                                                      meta.color
                                                                  )
                                                                      .backgroundColor,
                                                              borderColor:
                                                                  meta.color,
                                                          }
                                                        : undefined
                                                }
                                            >
                                                <input
                                                    type="radio"
                                                    name="appointmentStatus"
                                                    value={option.value}
                                                    checked={isSelected}
                                                    onChange={(event) =>
                                                        setStatus(
                                                            event.target.value
                                                        )
                                                    }
                                                    aria-describedby={
                                                        descriptionId
                                                    }
                                                    className="sr-only"
                                                />
                                                <span
                                                    className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full"
                                                    style={{
                                                        backgroundColor:
                                                            meta.color,
                                                    }}
                                                />
                                                <span className="min-w-0">
                                                    <span className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
                                                        {option.label}
                                                    </span>
                                                    <span
                                                        id={descriptionId}
                                                        className="mt-0.5 block text-xs leading-snug text-gray-500 dark:text-gray-300"
                                                    >
                                                        {option.description}
                                                    </span>
                                                </span>
                                            </label>
                                        )
                                    }
                                )}
                            </div>
                        </fieldset>

                        <div>
                            <div className="mb-2 flex items-center justify-between gap-3">
                                <label
                                    htmlFor="appointment-status-note"
                                    className="text-sm font-semibold text-gray-700 dark:text-gray-100"
                                >
                                    Note{' '}
                                    <span className="font-normal text-gray-400">
                                        (facultative)
                                    </span>
                                </label>
                                <span className="text-xs text-gray-400">
                                    {note.length}/
                                    {APPOINTMENT_STATUS_NOTE_MAX_LENGTH}
                                </span>
                            </div>
                            <Input
                                id="appointment-status-note"
                                name="appointmentStatusNote"
                                textArea
                                rows={4}
                                maxLength={APPOINTMENT_STATUS_NOTE_MAX_LENGTH}
                                value={note}
                                onChange={(event) =>
                                    setNote(event.target.value)
                                }
                                placeholder="Ajoutez un contexte utile pour l'équipe"
                            />
                        </div>

                        {error ? (
                            <Alert type="danger" showIcon>
                                {error}
                            </Alert>
                        ) : null}
                    </div>

                    <AppointmentStatusHistory
                        history={history}
                        preferences={preferences}
                    />
                </div>

                <div className="flex flex-col-reverse gap-2 border-t border-gray-100 pt-4 dark:border-gray-600 sm:flex-row sm:justify-end">
                    <Button
                        type="button"
                        className="w-full sm:w-auto"
                        disabled={isSubmitting}
                        onClick={handleClose}
                    >
                        Fermer
                    </Button>
                    <Button
                        type="submit"
                        className="w-full sm:w-auto"
                        variant="solid"
                        loading={isSubmitting}
                        disabled={!status || !hasChanges || isSubmitting}
                    >
                        Enregistrer le statut
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default AppointmentStatusDialog
